import { JwtPayload, jwtDecode } from 'jwt-decode';
import type { NavigateFunction } from 'react-router';
import Storage, { STORAGE_KEYS } from '@/utils/helpers/Storage';
import { LICENSE_TYPE, PLATFORM_ROLE_TYPE, SESSION_STORAGE_KEYS } from '@/consts/common';
import { withSafeResultSync } from '@/utils/helpers/safeResult';
import { safeJsonParse } from '@/utils/helpers/commonHelpers';
import { PlatformRoleType } from '@/services/users/types';
import { ILicenseType } from '@/services/licenses/types';

export interface CustomJwtPayload extends JwtPayload {
  'custom:user_uuid': string;
  'custom:org_uuid': string;
  'custom:is_profile_complete': string;
  'custom:platform_roles': string[];
  'custom:license_type': string;
}

export const PKCE = {
  generateVerifier: () => {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, Array.from(array)))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  },
  generateChallenge: async (verifier: string) => {
    const buffer = new TextEncoder().encode(verifier);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', buffer);
    return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(hashBuffer))))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  },
};

export const State = {
  generateStateParam: () => {
    const state = (Math.random() * 1e32).toString(36); // Random 19 character string
    Storage.setItem(STORAGE_KEYS.STATE, state);
    return state;
  },
  verifyStateParam: (state: string) => {
    const storedState = Storage.getItem(STORAGE_KEYS.STATE);
    const decodedState = atob(state);
    if (storedState === decodedState) {
      Storage.removeItem(STORAGE_KEYS.STATE);
      return true;
    }
    return false;
  },
};

/**
 *
 * @returns true if both access and refresh tokens are present in localStorage
 */
export function isAuthenticated(): boolean {
  const accessToken = Storage.getItem<string>(STORAGE_KEYS.ACCESS_TOKEN);
  const refreshToken = Storage.getItem<string>(STORAGE_KEYS.REFRESH_TOKEN);
  return !!accessToken && !!refreshToken;
}

export function getCurrentUuid(): string {
  const accessToken = Storage.getItem<string>(STORAGE_KEYS.ACCESS_TOKEN);
  if (!accessToken) return '';

  try {
    const decodedToken = jwtDecode<CustomJwtPayload>(accessToken);
    return decodedToken['custom:user_uuid'];
  } catch (error) {
    console.error('Error decoding token:', error);
    return '';
  }
}

export function isProfileComplete(): boolean {
  const accessToken = Storage.getItem<string>(STORAGE_KEYS.ACCESS_TOKEN);
  if (!accessToken) return false;
  const isProfileCompleteResult = withSafeResultSync(() => {
    const decodedToken = jwtDecode<CustomJwtPayload>(accessToken);
    return decodedToken['custom:is_profile_complete'];
  });
  if (!isProfileCompleteResult.ok) return false;
  return isProfileCompleteResult.data === 'true';
}

/**
 * @deprecated use canPerformAction(PERMISSION_ACTION.modifyPlatformRoles)
 * from @/lib/authZ instead
 * @returns false always
 */
export function hasAdminPlatform(): boolean {
  const accessToken = Storage.getItem<string>(STORAGE_KEYS.ACCESS_TOKEN);
  if (!accessToken) return false;
  const platformRolesResult = withSafeResultSync(() => {
    const decodedToken = jwtDecode<CustomJwtPayload>(accessToken);
    return decodedToken['custom:platform_roles'];
  });
  if (!platformRolesResult.ok) return false;
  const adminPlatform = safeJsonParse<PlatformRoleType[]>(platformRolesResult?.data);
  // return true;
  return adminPlatform?.some(
    (priv) => priv === PLATFORM_ROLE_TYPE.PLATFORM_ADMIN,
  ) ?? false;
}

export function isUnlicensedUser(): boolean {
  const accessToken = Storage.getItem<string>(STORAGE_KEYS.ACCESS_TOKEN);
  if (!accessToken) return false;
  const licenseTypeResult = withSafeResultSync(() => {
    const decodedToken = jwtDecode<CustomJwtPayload>(accessToken);
    return decodedToken['custom:license_type'];
  });
  if (!licenseTypeResult.ok) return false;
  const licenseType = safeJsonParse<ILicenseType>(licenseTypeResult?.data);
  return licenseType === LICENSE_TYPE.UNLICENSED;
}

export function isBasicLicensedUser(): boolean {
  const accessToken = Storage.getItem<string>(STORAGE_KEYS.ACCESS_TOKEN);
  if (!accessToken) return false;
  const licenseTypeResult = withSafeResultSync(() => {
    const decodedToken = jwtDecode<CustomJwtPayload>(accessToken);
    return decodedToken['custom:license_type'];
  });
  if (!licenseTypeResult.ok) return false;
  const licenseType = safeJsonParse<ILicenseType>(licenseTypeResult?.data);
  return licenseType === LICENSE_TYPE.BASIC;
}

export function navigateToRedirectPath(path: string, navigate: NavigateFunction) {
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.REDIRECT_PATH);
  navigate(path, { replace: true });
}

export function setCurrentLocationPath(path: string = '') {
  sessionStorage.setItem(SESSION_STORAGE_KEYS.REDIRECT_PATH, path);
}

export function getCurrentOrganizationUuid(): string {
  const accessToken = Storage.getItem<string>(STORAGE_KEYS.ACCESS_TOKEN);
  if (!accessToken) return '';
  const decodedToken = jwtDecode<CustomJwtPayload>(accessToken);
  return decodedToken['custom:org_uuid'];
}
